import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getDevices, getAlertJobs } from '../api/alertApi';
import StatusBadge from '../components/StatusBadge';

const STATUS_OPTIONS  = ['', 'pending', 'processing', 'sent', 'failed'];
const CHANNEL_OPTIONS = ['', 'whatsapp', 'email'];

export default function DeliveryStatus() {
  const [deviceId, setDeviceId] = useState('');
  const [status,   setStatus]   = useState('');
  const [channel,  setChannel]  = useState('');
  const [search,   setSearch]   = useState('');
  const [page,     setPage]     = useState(1);
  const [expanded, setExpanded] = useState(null);

  const { data: devicesData } = useQuery({ queryKey: ['devices'], queryFn: getDevices });
  const { data, isLoading }   = useQuery({
    queryKey: ['jobs', deviceId, status, channel, search, page],
    queryFn: () => getAlertJobs(deviceId || null, { status: status || undefined, channel: channel || undefined, search: search || undefined, page, limit: 25 }),
  });

  const devices = devicesData?.data || [];
  const jobs    = data?.data || [];
  const meta    = data?.meta || {};

  const reset = () => { setPage(1); setExpanded(null); };

  return (
    <div>
      <h1 style={{ marginBottom: 20 }}>Delivery Status</h1>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <input
          type="text"
          value={search}
          onChange={e => { setSearch(e.target.value); reset(); }}
          placeholder="Search by recipient or device ID…"
          style={{ ...sel, minWidth: 240 }}
        />
        <select value={deviceId} onChange={e => { setDeviceId(e.target.value); reset(); }} style={sel}>
          <option value="">All Devices</option>
          {devices.map(d => <option key={d.device_id} value={d.device_id}>{d.name} ({d.device_id})</option>)}
        </select>
        <select value={status} onChange={e => { setStatus(e.target.value); reset(); }} style={sel}>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s || 'All Statuses'}</option>)}
        </select>
        <select value={channel} onChange={e => { setChannel(e.target.value); reset(); }} style={sel}>
          {CHANNEL_OPTIONS.map(c => <option key={c} value={c}>{c || 'All Channels'}</option>)}
        </select>
      </div>

      {isLoading ? <p>Loading…</p> : (
        <>
          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>
            Showing {jobs.length} of {meta.total} deliveries
          </div>
          <table style={tbl}>
            <thead>
              <tr>
                {['#', 'Device', 'Channel', 'Recipient', 'Status', 'Attempts', 'Created', 'Logs'].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {jobs.map(j => (
                <>
                  <tr key={j.id} style={{ background: j.status === 'failed' ? '#fff5f5' : 'transparent' }}>
                    <td style={td}>{j.id}</td>
                    <td style={td}><code>{j.device_id}</code></td>
                    <td style={td}>
                      <span style={{ background: j.channel === 'whatsapp' ? '#dcfce7' : '#dbeafe', color: j.channel === 'whatsapp' ? '#166534' : '#1e40af', padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 600 }}>
                        {j.channel}
                      </span>
                    </td>
                    <td style={td}>
                      {j.recipient}
                      {j.is_test && (
                        <span style={{ marginLeft: 6, background: '#fef3c7', color: '#92400e', padding: '1px 6px', borderRadius: 8, fontSize: 11, fontWeight: 600 }}>
                          TEST
                        </span>
                      )}
                    </td>
                    <td style={td}><StatusBadge value={j.status} /></td>
                    <td style={td}>{j.attempts}/{j.max_attempts}</td>
                    <td style={td}>{j.processed_at ? new Date(j.processed_at).toLocaleString() : '—'}</td>
                    <td style={td}>
                      {j.logs?.length > 0 && (
                        <button style={logBtn} onClick={() => setExpanded(expanded === j.id ? null : j.id)}>
                          {expanded === j.id ? 'Hide' : `Show (${j.logs.length})`}
                        </button>
                      )}
                    </td>
                  </tr>
                  {expanded === j.id && j.logs?.map(log => (
                    <tr key={log.id} style={{ background: '#f9fafb' }}>
                      <td colSpan={8} style={{ padding: '8px 20px', fontSize: 12, borderBottom: '1px solid #e5e7eb' }}>
                        <strong>Attempt at {new Date(log.sent_at).toLocaleString()}</strong>
                        {' — '}
                        <StatusBadge value={log.status} />
                        {log.response_code && <span style={{ marginLeft: 8 }}>HTTP {log.response_code}</span>}
                        {log.error_message && <span style={{ marginLeft: 8, color: '#dc2626' }}>{log.error_message}</span>}
                        {log.response_body && (
                          <details style={{ marginTop: 4 }}>
                            <summary style={{ cursor: 'pointer', color: '#6b7280' }}>Response body</summary>
                            <pre style={{ fontSize: 11, overflow: 'auto', maxHeight: 120 }}>{log.response_body}</pre>
                          </details>
                        )}
                      </td>
                    </tr>
                  ))}
                </>
              ))}
              {jobs.length === 0 && (
                <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: '#9ca3af' }}>No deliveries found</td></tr>
              )}
            </tbody>
          </table>

          {meta.total > 25 && (
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'center' }}>
              <button style={pgBtn} disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
              <span style={{ lineHeight: '34px', fontSize: 14 }}>Page {meta.page} of {Math.ceil(meta.total / meta.limit)}</span>
              <button style={pgBtn} disabled={page * meta.limit >= meta.total} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const tbl   = { width: '100%', borderCollapse: 'collapse', fontSize: 13, background: '#fff' };
const th    = { padding: '8px 10px', background: '#f3f4f6', textAlign: 'left', fontWeight: 600 };
const td    = { padding: '8px 10px', borderBottom: '1px solid #e5e7eb', verticalAlign: 'top' };
const sel   = { padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, minWidth: 180 };
const pgBtn = { padding: '6px 14px', borderRadius: 6, border: '1px solid #d1d5db', cursor: 'pointer', background: '#fff' };
const logBtn = { padding: '3px 10px', borderRadius: 4, border: '1px solid #d1d5db', cursor: 'pointer', fontSize: 12, background: '#f9fafb' };
